// Shared, framework-free tab-resolution logic for the PerseusPay payment page.
//
// Extracted verbatim (behavior-preserving) from Payment.tsx so the tab rules
// live in ONE tested source instead of being re-implemented at each call site.
// See docs/PaymentPage-Optimization-Rollout-Plan.md §8.1.
//
// The core functions are pure — they take the parsed tab list / flags as
// explicit arguments and read no globals — so they are fully unit-testable.
// The `*FromHref` / `getTabsFromSearch` helpers are the only window-reading
// wrappers; they default to the live location but accept an override for tests.

import { TitleCase } from "../../../utils/Util";

/**
 * Tabs that participate in the save-card flow. Matches the original
 * Payment.tsx `isSaveCardTab` set exactly.
 */
export const SAVE_CARD_TABS = [
  "savecard",
  "manual",
  "savecardterminal",
  "terminal",
] as const;

/**
 * Parse the raw `?tabs=` query-string value into a lowercased list.
 * Mirrors the original `getTabsFromUrl`: an absent/empty value yields `[]`.
 */
export function parseTabsParam(tabsParam: string | null | undefined): string[] {
  return tabsParam ? tabsParam.split(";").map((tab) => tab.toLowerCase()) : [];
}

export function isSaveCardTab(tab: string): boolean {
  return (SAVE_CARD_TABS as readonly string[]).includes(tab.toLowerCase());
}

/**
 * Whether the tab strip should be shown.
 * Original `ShowTabs`: no/empty `?tabs=` => shown; a single tab => hidden;
 * two or more tabs => shown. Equivalent to "not exactly one tab".
 *
 * CreditReturn is the exception: its valid set is always Terminal + Token
 * regardless of `?tabs=`, so the strip must show even for a single (often
 * invalid, e.g. `?tabs=manual`) entry — otherwise the Token tab is rendered
 * but hidden behind the collapsed strip.
 */
export function shouldShowTabs(
  tabs: string[],
  transactionType?: string | null,
): boolean {
  if (transactionType === "CreditReturn") {
    // Bug 181752 (05): show the strip whenever at least one valid tab is visible,
    // so a single requested tab (e.g. ?tabs=Terminal) still shows its header
    // instead of hiding the strip entirely.
    return creditReturnVisibleTabs(tabs).length >= 1;
  } else if (transactionType === "CreditSale") {
    // Bug 181752 (05): show the strip whenever at least one valid tab is visible,
    // so a single requested tab (e.g. ?tabs=Manual) still shows its header
    // instead of hiding the strip entirely.
    return creditSaleVisibleTabs(tabs).length >= 1;
  }
  return tabs.length !== 1;
}

/**
 * The CreditReturn tabs that should actually be shown for a given `?tabs=` list.
 * Rule: keep the valid tabs requested (terminal/token) exactly as asked; only
 * when NONE of the requested tabs are valid (all invalid, or the list is empty)
 * expose the full set [terminal, token]. Values are lowercased. This is the
 * single source of the CreditReturn visibility rule shared by shouldShowTabs,
 * canShowTab, resolveDefaultTab, and Payment.tsx.
 */
export function creditReturnVisibleTabs(tabs: string[]): string[] {
  const valid = ["terminal", "token"];
  const requestedValid = tabs.filter((t) => valid.includes(t));
  return requestedValid.length > 0 ? requestedValid : valid;
}

/**
 * The CreditSale tabs that should actually be shown for a given `?tabs=` list.
 * Rule: keep the valid tabs requested (terminal/manual/sendlink/ach/token/payments)
 * exactly as asked; only when NONE of the requested tabs are valid (all invalid, or the list is empty)
 * expose the full set [terminal, manual, sendlink, ach, token, payments]. Values are lowercased.
 * This is the single source of the CreditSale visibility rule shared by shouldShowTabs,
 * canShowTab, resolveDefaultTab, and Payment.tsx.
 */
export function creditSaleVisibleTabs(tabs: string[]): string[] {
  const valid = ["terminal", "manual", "sendlink", "ach", "token", "payments"];
  const requestedValid = tabs.filter((t) => valid.includes(t));
  return requestedValid.length > 0 ? requestedValid : valid;
}

/**
 * The save-card tabs that should actually be shown for a given `?tabs=` list.
 * Mirrors creditReturnVisibleTabs/creditSaleVisibleTabs: keep the valid save-card
 * tabs requested (savecard/manual/savecardterminal/terminal) exactly as asked;
 * only when NONE of the requested tabs are valid (all invalid, or the list is
 * empty) expose the full default set. Values are lowercased.
 */
export function saveCardVisibleTabs(tabs: string[]): string[] {
  const valid = SAVE_CARD_TABS as readonly string[];
  const requestedValid = tabs.filter((t) => valid.includes(t));
  return requestedValid.length > 0 ? requestedValid : [...valid];
}

/**
 * Resolve the default (initially selected) tab.
 * Faithful extraction of the original `SetDefaultTab`; return type includes
 * `false` because the payment flow intentionally returns `false` when the
 * first tab is a save-card tab.
 */
export function resolveDefaultTab(
  isSaveCard: boolean,
  tabs: string[],
  transactionType?: string | null,
): string | false {
  const [firstTab] = tabs;

  if (isSaveCard) {
    // Bug 181752: pick the default from the FIRST VALID save-card tab requested,
    // not just tabs[0] — e.g. ?tabs=abc;savecard must land on Save Card, not fall
    // through to Save Card Terminal because the first (invalid) tab was ignored.
    // An empty list, or one with no valid save-card tab, defaults to Save Card
    // Terminal (the primary), matching saveCardVisibleTabs' default set.
    const requestedValid = tabs.filter((t) => isSaveCardTab(t));
    if (requestedValid.length === 0) return "Savecardterminal";
    return ["manual", "savecard"].includes(requestedValid[0])
      ? "Savecard"
      : "Savecardterminal";
  }

  // Payment flow. The original looped but always returned on the first
  // iteration, so only the first tab matters.
  if (tabs.length === 0) return "Terminal";

  if (transactionType === "CreditReturn") {
    // Default to a VISIBLE tab: Terminal when it's shown, otherwise the sole
    // visible tab (e.g. Token for `?tabs=Manual;Token`).
    const visible = creditReturnVisibleTabs(tabs);
    return visible.includes("terminal") ? "Terminal" : TitleCase(visible[0]);
  }

  // CreditSale: the save-card tabs (savecard/savecardterminal) belong to the
  // save-card action flow, not the payment portal, so they are invalid here.
  // Fall back to the payment-flow default (Terminal) instead of the broken
  // `false` below — the same "invalid tab → valid default" rule 181538 applied
  // to CreditReturn, now extended to CreditSale.
  if (transactionType === "CreditSale") {
    // Default to a VISIBLE tab: Terminal when it's shown, otherwise the sole
    // visible tab (e.g. Token for `?tabs=SaveCard;Manual`).
    const visible = creditSaleVisibleTabs(tabs);
    return visible.includes("terminal") ? "Terminal" : TitleCase(visible[0]);
  }
  if (["savecard", "savecardterminal"].includes(firstTab)) return false;
  return TitleCase(firstTab);
}

export interface TabVisibilityContext {
  /** True when the current URL is a save-card action (`.../savecard?...`). */
  isSaveCard: boolean;
  /** Parsed, lowercased `?tabs=` list. */
  tabs: string[];
  /** `TransactionType` claim decoded from the payment token, if any. */
  transactionType?: string | null;
}

/**
 * Whether a payment-flow tab (Terminal/Manual/Ach/Sendlink/Token/Payments)
 * should be rendered. Faithful extraction of the original `CheckTab`.
 */
export function canShowTab(tab: string, ctx: TabVisibilityContext): boolean {
  if (ctx.isSaveCard) return false;
  // CreditReturn is Terminal/Token only: show exactly the valid tabs requested,
  // or the full valid set when an invalid tab is present (or none requested).
  if (ctx.transactionType === "CreditReturn") {
    return creditReturnVisibleTabs(ctx.tabs).includes(tab.toLowerCase());
  } else if (ctx.transactionType === "CreditSale") {
    return creditSaleVisibleTabs(ctx.tabs).includes(tab.toLowerCase());
  }
  return ctx.tabs.length === 0 || ctx.tabs.includes(tab.toLowerCase());
}

/**
 * Whether a save-card-flow tab should be rendered.
 * Faithful extraction of the original `CheckSaveCardTabs`.
 */
export function canShowSaveCardTab(
  tab: string,
  ctx: Pick<TabVisibilityContext, "isSaveCard" | "tabs">,
): boolean {
  if (!ctx.isSaveCard) return false;
  if (!isSaveCardTab(tab)) return false;
  // Bug 181752 (03): an unrecognized ?tabs= value (no valid save-card tab) falls
  // back to the full default set, so the default valid tabs show instead of an
  // empty strip — matching the payment route's unrecognized-value behavior.
  return saveCardVisibleTabs(ctx.tabs).includes(tab.toLowerCase());
}

// --- window-reading convenience wrappers (default to the live location) ---

export function hrefIsSaveCardAction(
  href: string = window.location.href,
): boolean {
  return href.toLowerCase().includes("savecard?");
}

export function hrefIsPaymentAction(
  href: string = window.location.href,
): boolean {
  return href.toLowerCase().includes("payment?");
}

export function getTabsFromSearch(
  search: string = window.location.search,
): string[] {
  return parseTabsParam(new URLSearchParams(search).get("tabs"));
}
